import { collection, addDoc, serverTimestamp, query, orderBy, limit, getDocs, where, Timestamp } from 'firebase/firestore';
import { db } from './firebase';

/**
 * Logs an action to the audit_trail collection
 * @param {string} userId - The ID of the user performing the action
 * @param {string} userEmail - The email of the user
 * @param {string} action - The type of action (e.g., 'UPDATE_ROLE', 'DELETE_EVENT')
 * @param {string} target - What was acted upon (e.g., 'Member: Moctar Tall')
 * @param {object} details - Additional metadata about the action
 */
export const logAction = async (userId, userEmail, action, target, details = {}) => {
    try {
        if (!userId || !action) {
            console.warn('Audit: Missing required fields (userId or action)', { userId, action });
            // Continue usually, or return? Let's try to log what we have.
        }

        await addDoc(collection(db, 'audit_trail'), {
            userId: userId || 'unknown',
            userEmail: userEmail || 'unknown',
            action: action || 'UNKNOWN_ACTION',
            target: target || 'Unknown Target',
            details: details || {},
            timestamp: serverTimestamp()
        });
        console.log(`Audit logged: ${action} on ${target}`);
    } catch (error) {
        console.error('Error logging audit action:', error);
    }
};

/**
 * Logs a user connection to the connection_logs collection
 * @param {string} userId - The ID of the user
 * @param {string} userEmail - The email of the user
 * @param {string} fullName - The full name of the user
 * @param {string} action - LOGIN or LOGOUT
 * @param {object} metadata - Additional connection metadata (browser, device, etc.)
 */
export const logConnection = async (userId, userEmail, fullName, action = 'LOGIN', metadata = {}) => {
    try {
        // Get browser and device info
        const userAgent = window.navigator.userAgent;
        const platform = window.navigator.platform;
        const language = window.navigator.language;

        // Detect device type
        const isMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(userAgent);
        const isTablet = /iPad|Android(?!.*Mobile)/i.test(userAgent);
        const deviceType = isTablet ? 'tablet' : isMobile ? 'mobile' : 'desktop';

        // Detect browser
        let browser = 'Unknown';
        if (userAgent.includes('Firefox')) browser = 'Firefox';
        else if (userAgent.includes('Opera') || userAgent.includes('OPR')) browser = 'Opera';
        else if (userAgent.includes('Edge')) browser = 'Edge';
        else if (userAgent.includes('Chrome')) browser = 'Chrome';
        else if (userAgent.includes('Safari')) browser = 'Safari';

        await addDoc(collection(db, 'connection_logs'), {
            userId,
            userEmail,
            fullName: fullName || userEmail,
            action,
            deviceType,
            browser,
            platform,
            language,
            userAgent,
            timestamp: serverTimestamp(),
            ...metadata
        });
    } catch (error) {
        console.error('Error logging connection:', error);
    }
};

export const fetchLogs = async (maxLogs = 100) => {
    try {
        const q = query(
            collection(db, 'audit_trail'),
            orderBy('timestamp', 'desc'),
            limit(maxLogs)
        );
        const snapshot = await getDocs(q);
        return snapshot.docs.map(doc => ({
            id: doc.id,
            ...doc.data(),
            timestamp: doc.data().timestamp?.toDate() || new Date()
        }));
    } catch (error) {
        console.error('Error fetching audit logs:', error);
        return [];
    }
};

/**
 * Fetch connection logs with optional date filtering
 * @param {number} maxLogs - Maximum number of logs to fetch
 * @param {Date} startDate - Optional start date filter
 * @param {Date} endDate - Optional end date filter
 */
export const fetchConnectionLogs = async (maxLogs = 500, startDate = null, endDate = null) => {
    try {
        let q;

        if (startDate && endDate) {
            q = query(
                collection(db, 'connection_logs'),
                where('timestamp', '>=', Timestamp.fromDate(startDate)),
                where('timestamp', '<=', Timestamp.fromDate(endDate)),
                orderBy('timestamp', 'desc'),
                limit(maxLogs)
            );
        } else {
            q = query(
                collection(db, 'connection_logs'),
                orderBy('timestamp', 'desc'),
                limit(maxLogs)
            );
        }

        const snapshot = await getDocs(q);
        return snapshot.docs.map(doc => ({
            id: doc.id,
            ...doc.data(),
            timestamp: doc.data().timestamp?.toDate() || new Date()
        }));
    } catch (error) {
        console.error('Error fetching connection logs:', error);
        return [];
    }
};

/**
 * Get connection statistics for a given period
 * @param {Date} startDate - Start of period
 * @param {Date} endDate - End of period
 */
export const getConnectionStats = async (startDate, endDate) => {
    const logs = await fetchConnectionLogs(1000, startDate, endDate);

    // Stats de base
    const totalConnections = logs.filter(l => l.action === 'LOGIN').length;
    const uniqueUsers = new Set(logs.map(l => l.userId)).size;

    // Connexions par heure (0-23)
    const connectionsByHour = Array(24).fill(0);
    logs.filter(l => l.action === 'LOGIN').forEach(log => {
        const hour = log.timestamp.getHours();
        connectionsByHour[hour]++;
    });

    // Connexions par jour de la semaine (0=Dim, 1=Lun, etc.)
    const connectionsByDay = Array(7).fill(0);
    logs.filter(l => l.action === 'LOGIN').forEach(log => {
        const day = log.timestamp.getDay();
        connectionsByDay[day]++;
    });

    // Connexions par appareil
    const connectionsByDevice = { mobile: 0, tablet: 0, desktop: 0 };
    logs.filter(l => l.action === 'LOGIN').forEach(log => {
        if (log.deviceType && connectionsByDevice.hasOwnProperty(log.deviceType)) {
            connectionsByDevice[log.deviceType]++;
        }
    });

    // Connexions par navigateur
    const connectionsByBrowser = {};
    logs.filter(l => l.action === 'LOGIN').forEach(log => {
        const browser = log.browser || 'Unknown';
        connectionsByBrowser[browser] = (connectionsByBrowser[browser] || 0) + 1;
    });

    // Top utilisateurs (les plus actifs)
    const userActivity = {};
    logs.filter(l => l.action === 'LOGIN').forEach(log => {
        if (!userActivity[log.userId]) {
            userActivity[log.userId] = {
                userId: log.userId,
                email: log.userEmail,
                fullName: log.fullName,
                count: 0,
                lastConnection: log.timestamp
            };
        }
        userActivity[log.userId].count++;
        if (log.timestamp > userActivity[log.userId].lastConnection) {
            userActivity[log.userId].lastConnection = log.timestamp;
        }
    });

    const topUsers = Object.values(userActivity)
        .sort((a, b) => b.count - a.count)
        .slice(0, 10);

    // Connexions par date (pour graphique de tendance)
    const connectionsByDate = {};
    logs.filter(l => l.action === 'LOGIN').forEach(log => {
        const date = log.timestamp.toISOString().split('T')[0];
        connectionsByDate[date] = (connectionsByDate[date] || 0) + 1;
    });

    // Convertir en tableau trié
    const dailyTrend = Object.entries(connectionsByDate)
        .map(([date, count]) => ({ date, count }))
        .sort((a, b) => a.date.localeCompare(b.date));

    return {
        totalConnections,
        uniqueUsers,
        connectionsByHour,
        connectionsByDay,
        connectionsByDevice,
        connectionsByBrowser,
        topUsers,
        dailyTrend,
        recentLogs: logs.slice(0, 50)
    };
};
